import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Sparkles,
    RefreshCcw,
    X,
    Copy,
    Check,
    RotateCw,
    ChevronDown,
} from "lucide-react";
import ToneChip from "@/components/ToneChip";
import SendPanel from "@/components/SendPanel";
import { copyTextToClipboard } from "@/lib/clipboard";
import { INTENT_TO_STYLE } from "@/lib/intentMeta";
import { track, bumpLocalCounter } from "@/lib/analytics";
import {
    resolveLayer1Display,
    markLayer1Displayed,
} from "@/lib/layer1Display";

const STYLE_DESCRIPTIONS = {
    concise: "short & direct",
    warm: "friendly & empathetic",
    firm: "polite but assertive",
    professional: "formal business register",
    gentle: "gentle & affectionate",
};

const AnalysisCard = ({
    analysis,
    onSendOriginal,
    onUseImproved,
    onDismiss,
    onRegenerate,
    regenerating = false,
    preferredStyle = null,
    defaultStyle = null,
    intent = null,
    regenCountByStyle = {},
    regenLimit = 5,
    chatId = null,
    // Active chat object (carries `phone_number` + `email_address` for the
    // SendPanel's Direct Send section). Optional — falls back to copy-only.
    activeChat = null,
    // User-facing communication style key (calm | direct | warm |
    // professional) — kept separate from `defaultStyle` (which carries the
    // internal rewrite-style key like gentle/concise/warm/firm/professional
    // used for tab selection logic above). Layer 1's general_awareness
    // fallback library is keyed by the user-facing style.
    userStyle = null,
    // Relationship register for the active chat — feeds the Love-intent
    // register modifier line (closed library). Optional; falls back to
    // the bare Love line when null.
    chatRegister = null,
}) => {
    const versions = useMemo(() => {
        if (analysis?.versions?.length) return analysis.versions;
        if (analysis?.improved) {
            return [{ style: "concise", label: "Concise", text: analysis.improved }];
        }
        return [];
    }, [analysis]);

    const initialStyle = useMemo(() => {
        // 1. Honor a pinned user preference if it matches a returned version.
        if (
            preferredStyle &&
            versions.some((v) => v.style === preferredStyle)
        ) {
            return preferredStyle;
        }
        // 2. Honor the user's global default style (set in Profile panel)
        //    when it's a concrete style (not "auto").
        if (
            defaultStyle &&
            defaultStyle !== "auto" &&
            versions.some((v) => v.style === defaultStyle)
        ) {
            return defaultStyle;
        }
        // 3. Otherwise, if the user picked an intent, default to its mapped style.
        const mapped = intent ? INTENT_TO_STYLE[intent] : null;
        if (mapped && versions.some((v) => v.style === mapped)) {
            return mapped;
        }
        // 4. Fall back to the first available rewrite.
        return versions[0]?.style || "concise";
    }, [versions, preferredStyle, defaultStyle, intent]);

    const [selectedStyle, setSelectedStyle] = useState(initialStyle);
    const [copied, setCopied] = useState(false);
    const [showCoaching, setShowCoaching] = useState(false);

    // Reset selection whenever a new analysis arrives — honoring the user's preferred style.
    useEffect(() => {
        setSelectedStyle(initialStyle);
        setCopied(false);
        setShowCoaching(false);
    }, [analysis?.id, initialStyle]);

    // Layer 1 display discipline (additive — does NOT touch analysis or
    // sessionLog). Decides whether the coaching/rationale section
    // surfaces at all + whether to render the `general_awareness`
    // closed-library fallback when the backend returned no specific
    // guidance. Re-evaluated when a NEW analysis.id arrives, so
    // regenerating the rewrite (same analysis.id) preserves the previous
    // decision verbatim (Fix 1).
    const layer1Display = useMemo(() => {
        if (!analysis) {
            return {
                shouldDisplay: false,
                flag: null,
                fallbackLine: null,
                suppressionReason: "no_flag",
            };
        }
        return resolveLayer1Display({
            chatId,
            analysis,
            currentStyle: userStyle,
            register: chatRegister,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis?.id, chatId, userStyle, chatRegister]);

    // Persist "this analysis's flag was displayed" exactly once per
    // analysis.id — idempotent guard inside markLayer1Displayed keeps
    // StrictMode double-renders safe.
    useEffect(() => {
        if (!analysis?.id || !layer1Display.shouldDisplay) return;
        markLayer1Displayed({
            chatId,
            analysisId: analysis.id,
            flag: layer1Display.flag,
        });
    }, [
        analysis?.id,
        chatId,
        layer1Display.shouldDisplay,
        layer1Display.flag,
    ]);

    if (!analysis) return null;

    const selected =
        versions.find((v) => v.style === selectedStyle) || versions[0];

    const handleCopy = async () => {
        if (!selected?.text) return;
        const ok = await copyTextToClipboard(selected.text);
        if (ok) {
            setCopied(true);
            toast.success(`${selected.label} rewrite copied`);
            track("message_copied", {
                variant: "analysis_preview",
                style: selected.style,
            });
            bumpLocalCounter("message_copied");
            window.setTimeout(() => setCopied(false), 1600);
        } else {
            toast.error("Couldn't copy — please select and copy manually.");
        }
    };

    return (
        <div
            data-testid="analysis-card"
            className="tg-enter mx-auto w-full bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm flex flex-col gap-5"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                        <h3 className="font-serif text-lg leading-tight text-foreground">
                            Tone analysis
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {analysis.label}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ToneChip
                        signal={analysis.detected_signal}
                        risk={analysis.risk_level}
                        riskLabel={analysis.risk_label}
                        tone={analysis.tone}
                    />
                    <button
                        data-testid="analysis-dismiss-btn"
                        onClick={onDismiss}
                        aria-label="Dismiss"
                        className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <div
                    data-testid="analysis-original"
                    className="rounded-xl border border-border bg-muted/50 p-4"
                >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">
                        Original
                    </p>
                    <p className="text-[15px] leading-relaxed text-foreground/70 italic whitespace-pre-wrap">
                        “{analysis.original}”
                    </p>
                </div>
                <div
                    data-testid="analysis-improved"
                    className="rounded-xl border border-[#D5E3D1] bg-[#EAF1E8]/60 p-4 flex flex-col gap-3"
                >
                    <div className="flex items-center justify-between gap-2">
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#4B6143]">
                                Suggested rewrites
                            </p>
                            <p className="text-[11px] text-[#4B6143]/70 mt-0.5">
                                {selected
                                    ? STYLE_DESCRIPTIONS[selected.style] || ""
                                    : ""}
                            </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {(() => {
                                const used = regenCountByStyle[selectedStyle] || 0;
                                const atLimit = used >= regenLimit;
                                return (
                                    <button
                                        data-testid="btn-regenerate"
                                        data-style={selectedStyle}
                                        data-regen-count={used}
                                        type="button"
                                        onClick={() =>
                                            onRegenerate &&
                                            onRegenerate(selectedStyle)
                                        }
                                        disabled={regenerating || atLimit}
                                        aria-label={
                                            atLimit
                                                ? `${selected?.label || selectedStyle} has 5 of 5 variations`
                                                : `Regenerate ${selected?.label || selectedStyle}`
                                        }
                                        title={
                                            atLimit
                                                ? "5 of 5 — try a different tone"
                                                : `Regenerate this tone (${used}/${regenLimit})`
                                        }
                                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#4B6143] hover:text-[#2f3d29] bg-white/60 hover:bg-white border border-[#D5E3D1] rounded-full px-2.5 py-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        <RotateCw
                                            className={`h-3 w-3 ${regenerating ? "animate-spin" : ""}`}
                                        />
                                        {regenerating
                                            ? "Regenerating"
                                            : atLimit
                                              ? `5/5 — switch tone`
                                              : `Regenerate (${used}/${regenLimit})`}
                                    </button>
                                );
                            })()}
                            <button
                                data-testid="btn-copy-improved"
                                type="button"
                                onClick={handleCopy}
                                aria-label="Copy selected rewrite"
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#4B6143] hover:text-[#2f3d29] bg-white/60 hover:bg-white border border-[#D5E3D1] rounded-full px-2.5 py-1 transition-colors"
                            >
                                {copied ? (
                                    <>
                                        <Check className="h-3 w-3" />
                                        Copied
                                    </>
                                ) : (
                                    <>
                                        <Copy className="h-3 w-3" />
                                        Copy
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div
                        data-testid="rewrite-style-tabs"
                        role="tablist"
                        className="flex flex-wrap items-center gap-1.5"
                    >
                        {versions.map((v) => {
                            const active = v.style === selectedStyle;
                            return (
                                <button
                                    key={v.style}
                                    role="tab"
                                    aria-selected={active}
                                    data-testid={`rewrite-tab-${v.style}`}
                                    data-active={active}
                                    onClick={() => setSelectedStyle(v.style)}
                                    className={`text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border transition-colors ${
                                        active
                                            ? "bg-[#5E7153] text-white border-[#5E7153]"
                                            : "bg-white/60 text-[#4B6143] border-[#D5E3D1] hover:bg-white"
                                    }`}
                                >
                                    {v.label}
                                </button>
                            );
                        })}
                    </div>

                    <p
                        data-testid="rewrite-selected-text"
                        className={`text-[15px] leading-relaxed text-[#2f3d29] whitespace-pre-wrap transition-opacity ${
                            regenerating ? "opacity-40" : "opacity-100"
                        }`}
                    >
                        {selected?.text}
                    </p>
                </div>
            </div>

            {layer1Display.shouldDisplay &&
                (layer1Display.loveLine ? (
                    <div
                        data-testid="coaching-section"
                        data-flag={layer1Display.flag}
                        className="rounded-xl border border-rose-200/70 bg-rose-50/40 px-4 py-3"
                    >
                        <p
                            data-testid="layer1-love-line"
                            className="text-[13px] leading-relaxed text-foreground"
                        >
                            <span className="inline-flex items-center gap-2 mr-2 text-[12px] uppercase tracking-[0.08em] font-semibold text-rose-600/80">
                                <Sparkles className="h-3.5 w-3.5 text-rose-500" />
                                For this kind of message
                            </span>
                            {layer1Display.loveLine}
                        </p>
                        {layer1Display.loveRegisterLine && (
                            <p
                                data-testid="layer1-love-register-line"
                                className="mt-2.5 text-[12.5px] leading-relaxed text-rose-700/90 italic"
                            >
                                {layer1Display.loveRegisterLine}
                            </p>
                        )}
                    </div>
                ) : layer1Display.flag === "general_awareness" ? (
                    <div
                        data-testid="coaching-section"
                        data-flag="general_awareness"
                        className="rounded-xl border border-border bg-muted/30 px-4 py-3"
                    >
                        <p
                            data-testid="layer1-general-awareness"
                            className="text-[13px] leading-relaxed text-foreground/80"
                        >
                            <span className="inline-flex items-center gap-2 mr-2 text-[12px] uppercase tracking-[0.08em] font-semibold text-foreground/60">
                                <Sparkles className="h-3.5 w-3.5 text-primary" />
                                Worth a glance
                            </span>
                            {layer1Display.fallbackLine}
                        </p>
                    </div>
                ) : (
                    (analysis.coaching?.length || analysis.rationale) && (
                        <div
                            data-testid="coaching-section"
                            data-flag={layer1Display.flag}
                            className="rounded-xl border border-border bg-muted/30"
                        >
                            <button
                                data-testid="btn-why-this-tone"
                                type="button"
                                onClick={() => setShowCoaching((s) => !s)}
                                aria-expanded={showCoaching}
                                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-foreground/70 hover:text-foreground transition-colors"
                            >
                                <span className="inline-flex items-center gap-2">
                                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                                    Why this tone?
                                </span>
                                <ChevronDown
                                    className={`h-4 w-4 transition-transform ${
                                        showCoaching ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {showCoaching && (
                                <div className="tg-enter px-4 pb-4 border-t border-border">
                                    {analysis.coaching?.length > 0 && (
                                        <ul
                                            data-testid="coaching-bullets"
                                            className="pt-3 space-y-2 text-[13px] leading-relaxed text-foreground/80"
                                        >
                                            {analysis.coaching
                                                .slice(0, 3)
                                                .map((b, i) => (
                                                    <li
                                                        key={i}
                                                        data-testid={`analysis-coaching-bullet-${i}`}
                                                        className="flex gap-2.5"
                                                    >
                                                        <span
                                                            aria-hidden
                                                            className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#5E7153] shrink-0"
                                                        />
                                                        <span>{b}</span>
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                    {analysis.rationale && (
                                        <p className="mt-3 pt-3 border-t border-border/70 text-xs text-muted-foreground italic">
                                            {analysis.rationale}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                ))}

            <SendPanel
                chat={activeChat}
                improvedText={selected?.text || ""}
                originalText={analysis?.original || ""}
                style={selected?.style || null}
                intent={intent || null}
                messageId={analysis?.id || null}
                onAfterAccept={() => onUseImproved(selected)}
                onAfterOriginal={() => onSendOriginal?.()}
            />
        </div>
    );
};

export default AnalysisCard;
